export type InvitationStatus = 'open' | 'closed';
export type WhoInvites = 'group_coordinator' | 'i_email';
export type WhoPays = 'i_pay' | 'participant_pays';
export type HowResults = 'i_present' | 'instant_profile' | 'instant_profile_slideshow';
export type OrganizerRole = 'owner' | 'presenter';
export type ParticipantState = 'not_started' | 'started' | 'scored';

/** Rows of the detail page's "Reminders & Messages" table — each is an editable email template. */
export type MessageKey =
  'participant_invitation' | 'participant_reminder' | 'thinking_tips';

export interface EmailTemplate {
  subject: string;
  body: string;
}

export interface ScheduleStep {
  id: string;
  step: number;
  name: string;        // 'deadline' | 'presentation'
  eventName: string;   // 'Participant deadline'
  date: string;        // ISO
}

export interface Organizer {
  id: string;
  name: string;
  email: string;
  role: OrganizerRole;
  notify: boolean;
  accepted?: boolean;   // false = invited but hasn't approved yet (pending); undefined/true = approved
}

export interface Participant {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  state: ParticipantState;
  profileType?: string;
  profileTypeId?: number; // 1-15, drives the FourSight energy-wave icon (app-fs-icon)
  followupDisabled?: boolean; // per-participant follow-up email opt-out
  answered?: number; // assessment items answered so far (only meaningful while 'started')
  lastActivity?: string; // ISO
  scores?: { cl: number; id: number; dv: number; im: number }; // FourSight preference scores (only when scored)
  unlocked?: boolean; // scored profiles only: true once a scoring key has been spent to reveal results.
                      // A scored profile is "locked" while this is falsy.
}

export interface Invitation {
  id: string;
  name: string;
  rosterId: string;
  rosterName: string;
  language: string;
  status: InvitationStatus;
  estimatedParticipants: number;
  whoInvites: WhoInvites;
  whoPays: WhoPays;
  howResults: HowResults;
  schedule: ScheduleStep[];
  organizers: Organizer[];
  participants: Participant[];
  email: { subject: string; body: string };
  // Per-message overrides of the default reminder/notice templates ('participant_invitation'
  // lives in `email` above). Optional so pre-existing localStorage data stays valid.
  messages?: Partial<Record<MessageKey, EmailTemplate>>;
  scoringKeys: { available: number; needed: number };
  followupEmailsEnabled: boolean;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RosterRef {
  id: string;
  name: string;
  availableKeys: number;
}

export interface InvitationQuery {
  search?: string;
  status?: InvitationStatus | 'all';
  archived?: boolean;
  sort?: { prop: string; dir: 'asc' | 'desc' }; // prop = keyof Invitation, or 'deadline' / 'presentation'
  page?: number;     // 0-based
  pageSize?: number;
}

/** Payload to create an invitation from the wizard. */
export interface InvitationDraft {
  name: string;
  rosterId: string;
  language: string;
  estimatedParticipants: number;
  whoInvites: WhoInvites;
  whoPays: WhoPays;
  howResults: HowResults;
  schedule: ScheduleStep[];
  organizers: Organizer[];
  email: { subject: string; body: string };
  messages?: Partial<Record<MessageKey, EmailTemplate>>;
  followupEmailsEnabled: boolean;
}

export const ROLE_LABELS: Record<OrganizerRole, string> = {
  owner: 'Invitation Admin', presenter: 'Organizer',
};

export const STATUS_LABELS: Record<InvitationStatus, string> = {
  open: 'Active', closed: 'Closed',
};

export const WHO_INVITES_LABELS: Record<WhoInvites, string> = {
  group_coordinator: 'Group coordinator emails participants',
  i_email: 'I email participants',
};

export const WHO_PAYS_LABELS: Record<WhoPays, string> = {
  i_pay: 'I pay for the participants',
  participant_pays: 'Participant pays before taking assessment',
};

export const HOW_RESULTS_LABELS: Record<HowResults, string> = {
  i_present: 'I will present the results',
  instant_profile: 'Participants get results instantly (profile only)',
  instant_profile_slideshow: 'Participants get results instantly (profile + personal slideshow)',
};

/**
 * How an activity group splits its participants. Two options only — grouping BY thinking
 * preference is the point of the feature. `diverse_lottery`, `alphabetical` and `manual` were
 * removed in v16.2.082: lottery was `diverse_exact` with the order shuffled, alphabetical carried
 * no preference logic at all, and manual just made empty boxes to drag into.
 */
export type SplitType = 'similar' | 'diverse_exact';

/** One of the four FourSight preferences. */
export type Preference = 'cl' | 'id' | 'dv' | 'im';

export interface SessionGroup {
  id: string;            // 'g-1' — minted per split, not via newId()
  name: string;          // 'Group 1' — renameable
  memberIds: string[];   // Participant.id, ordered
}

/**
 * Which preferences each profile type is HIGH in — the type IS the peak set.
 * 4 one-peak, 6 two-peak, 4 three-peak, Integrator with none.
 *
 * Sourced from cig-congratulations.component.html:16-17, which keys by id and agrees with
 * PROFILE_IDS and fs-icon.component.ts. Deliberately NOT from cig-slideshow.service.ts, whose
 * Driver/Finisher keys are swapped (TODO #23) — Driver (9) is Ideate+Implement, Finisher (10)
 * is Develop+Implement.
 *
 * The frontend never derives peaks from scores: it does no scoring at all.
 */
export const PEAK_SETS: Record<number, Preference[]> = {
  1: ['cl'],                    // Clarifier
  2: ['id'],                    // Ideator
  3: ['dv'],                    // Developer
  4: ['im'],                    // Implementer
  5: ['cl', 'id'],              // Early Bird
  6: ['cl', 'dv'],              // Analyst
  7: ['cl', 'im'],              // Accelerator
  8: ['id', 'dv'],              // Theorist
  9: ['id', 'im'],              // Driver
  10: ['dv', 'im'],             // Finisher
  11: ['cl', 'id', 'dv'],       // Hare
  12: ['cl', 'id', 'im'],       // Idea Broker
  13: ['id', 'dv', 'im'],       // Optimist
  14: [],                       // Integrator — balanced, no peak
  15: ['cl', 'dv', 'im'],       // Realist
};

/** A participant's peaks. Unknown/absent type → no peaks (treated like an Integrator). */
export function peaksFor(profileTypeId?: number): Preference[] {
  return (profileTypeId != null && PEAK_SETS[profileTypeId]) || [];
}

export const PREFERENCE_LABELS: Record<Preference, string> = {
  cl: 'Clarify', id: 'Ideate', dv: 'Develop', im: 'Implement',
};
